import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import Navbar from '../../shared/navbar';
import './MaintenanceDashboard.css';

// ─── helpers ────────────────────────────────────────────────────────────────

const PRIORITY_COLORS = { urgent: '#7c3aed', high: '#dc2626', medium: '#d97706', low: '#16a34a' };
const STATUS_COLS = [
    { key: 'pending',     label: 'Pending',     color: '#3b82f6' },
    { key: 'in_progress', label: 'In Progress',  color: '#f59e0b' },
    { key: 'completed',   label: 'Completed',    color: '#10b981' },
    { key: 'cancelled',   label: 'Cancelled',    color: '#6b7280' },
];

const getCategoryFromType = (t = '') => {
    const s = t.toLowerCase();
    if (s.includes('hvac') || s.includes('ac') || s.includes('heat') || s.includes('cool')) return 'HVAC';
    if (s.includes('plumb') || s.includes('water') || s.includes('faucet') || s.includes('pipe') || s.includes('leak')) return 'Plumbing';
    if (s.includes('electr') || s.includes('light') || s.includes('outlet') || s.includes('wiring')) return 'Electrical';
    if (s.includes('appliance')) return 'Appliance';
    if (s.includes('structural') || s.includes('wall') || s.includes('door') || s.includes('window') || s.includes('roof')) return 'Structural';
    if (s.includes('pest') || s.includes('rodent') || s.includes('bug')) return 'Pest Control';
    if (s.includes('clean') || s.includes('janitorial')) return 'Cleaning';
    return 'General';
};

const CATEGORY_ICONS = {
    HVAC: '🌡️', Plumbing: '🔧', Electrical: '⚡', Appliance: '🔌',
    Structural: '🏗️', 'Pest Control': '🐛', Cleaning: '🧹', General: '🔩',
};

const timeAgo = (dateStr) => {
    if (!dateStr) return 'N/A';
    const diff = Date.now() - new Date(dateStr).getTime();
    const d = Math.floor(diff / 86400000);
    if (d === 0) return 'Today';
    if (d === 1) return 'Yesterday';
    if (d < 30) return `${d}d ago`;
    if (d < 365) return `${Math.floor(d / 30)}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
};

const fmt = (n) => (n == null ? 'N/A' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);

const isOverdue = (r) =>
    r.status !== 'completed' && r.status !== 'cancelled' &&
    r.scheduled_date && new Date(r.scheduled_date) < new Date();

// ─── Sub-components ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color, icon }) {
    return (
        <div className="mq-kpi-card" style={{ borderTop: `3px solid ${color}` }}>
            <div className="mq-kpi-icon">{icon}</div>
            <div>
                <div className="mq-kpi-value" style={{ color }}>{value}</div>
                <div className="mq-kpi-label">{label}</div>
                {sub && <div className="mq-kpi-sub">{sub}</div>}
            </div>
        </div>
    );
}

function PriorityBadge({ priority }) {
    const color = PRIORITY_COLORS[priority] || '#6b7280';
    return <span className="mq-badge" style={{ background: color + '20', color, border: `1px solid ${color}40` }}>{priority}</span>;
}

function StatusBadge({ status }) {
    const col = STATUS_COLS.find(s => s.key === status) || { color: '#6b7280', label: status };
    return <span className="mq-badge" style={{ background: col.color + '20', color: col.color, border: `1px solid ${col.color}40` }}>{col.label}</span>;
}

// ─── Request Detail Modal ─────────────────────────────────────────────────────

function RequestDetailModal({ request, onClose, onStatusChange }) {
    const [newStatus, setNewStatus] = useState(request.status);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            if (request.status !== newStatus) {
                await fetch(`${API_BASE_URL}/api/maintenance-requests/${request.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: newStatus }),
                });
            }
            if (note.trim()) {
                await fetch(`${API_BASE_URL}/api/maintenance-requests/${request.id}/updates`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ description: note, update_type: 'note' }),
                });
            }
            onStatusChange();
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mq-modal-overlay" onClick={onClose}>
            <div className="mq-modal" onClick={e => e.stopPropagation()}>
                <div className="mq-modal-header">
                    <div>
                        <h2>{CATEGORY_ICONS[getCategoryFromType(request.request_type)]} {request.request_type}</h2>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <PriorityBadge priority={request.priority} />
                            <StatusBadge status={request.status} />
                        </div>
                    </div>
                    <button className="mq-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="mq-modal-body">
                    <div className="mq-detail-grid">
                        <div className="mq-detail-item"><span className="mq-detail-label">Property</span><span>{request.property_name || 'N/A'}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Unit</span><span>{request.unit_number || 'N/A'}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Tenant</span><span>{request.tenant_name || 'N/A'}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Assigned To</span><span>{request.assigned_to || 'Unassigned'}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Scheduled</span><span>{request.scheduled_date || 'Not scheduled'}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Completed</span><span>{request.completed_date || '—'}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Est. Cost</span><span>{fmt(request.estimated_cost)}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Actual Cost</span><span>{fmt(request.actual_cost)}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Submitted</span><span>{timeAgo(request.created_at)}</span></div>
                        <div className="mq-detail-item"><span className="mq-detail-label">Category</span><span>{getCategoryFromType(request.request_type)}</span></div>
                    </div>

                    <div className="mq-detail-description">
                        <div className="mq-detail-label" style={{ marginBottom: 6 }}>Description</div>
                        <p>{request.description}</p>
                    </div>

                    {request.notes && (
                        <div className="mq-detail-description">
                            <div className="mq-detail-label" style={{ marginBottom: 6 }}>Notes</div>
                            <p>{request.notes}</p>
                        </div>
                    )}

                    {request.updates?.length > 0 && (
                        <div className="mq-updates-section">
                            <div className="mq-detail-label" style={{ marginBottom: 8 }}>Activity Log</div>
                            {request.updates.map((u, i) => (
                                <div key={i} className="mq-update-item">
                                    <span className="mq-update-type">{u.update_type}</span>
                                    <span className="mq-update-desc">{u.description}</span>
                                    <span className="mq-update-time">{timeAgo(u.created_at)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mq-modal-actions">
                        <div className="mq-modal-field">
                            <label>Update Status</label>
                            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="mq-select">
                                {STATUS_COLS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="mq-modal-field">
                            <label>Add Note (optional)</label>
                            <textarea className="mq-textarea" value={note} onChange={e => setNote(e.target.value)} placeholder="Add a progress note..." rows={3} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="mq-btn-secondary" onClick={onClose}>Cancel</button>
                            <button className="mq-btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── New Request Modal ────────────────────────────────────────────────────────

function NewRequestModal({ properties, tenants, onClose, onCreated }) {
    const [form, setForm] = useState({
        property_id: '', unit_number: '', request_type: '', priority: 'medium',
        description: '', tenant_id: '', scheduled_date: '', assigned_to: '',
        estimated_cost: '', notes: '',
    });
    const [saving, setSaving] = useState(false);

    const handle = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/maintenance-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, status: 'pending' }),
            });
            if (!res.ok) throw new Error('Failed to create request');
            onCreated();
            onClose();
        } catch (e) {
            alert(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mq-modal-overlay" onClick={onClose}>
            <div className="mq-modal mq-modal-wide" onClick={e => e.stopPropagation()}>
                <div className="mq-modal-header">
                    <h2>New Maintenance Request</h2>
                    <button className="mq-close-btn" onClick={onClose}>✕</button>
                </div>
                <div className="mq-modal-body">
                    <form onSubmit={submit}>
                        <div className="mq-form-grid">
                            <div className="mq-modal-field">
                                <label>Property *</label>
                                <select name="property_id" value={form.property_id} onChange={handle} className="mq-select" required>
                                    <option value="">Select property…</option>
                                    {properties.map(p => <option key={p.id} value={p.id}>{p.property_name || p.name}</option>)}
                                </select>
                            </div>
                            <div className="mq-modal-field">
                                <label>Unit Number</label>
                                <input name="unit_number" value={form.unit_number} onChange={handle} className="mq-input" placeholder="e.g. 4B" />
                            </div>
                            <div className="mq-modal-field">
                                <label>Request Type *</label>
                                <input name="request_type" value={form.request_type} onChange={handle} className="mq-input" placeholder="e.g. Plumbing – leaky faucet" required />
                            </div>
                            <div className="mq-modal-field">
                                <label>Priority *</label>
                                <select name="priority" value={form.priority} onChange={handle} className="mq-select" required>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                            <div className="mq-modal-field">
                                <label>Tenant (optional)</label>
                                <select name="tenant_id" value={form.tenant_id} onChange={handle} className="mq-select">
                                    <option value="">No tenant linked</option>
                                    {tenants.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                                </select>
                            </div>
                            <div className="mq-modal-field">
                                <label>Assigned To</label>
                                <input name="assigned_to" value={form.assigned_to} onChange={handle} className="mq-input" placeholder="Contractor / staff name" />
                            </div>
                            <div className="mq-modal-field">
                                <label>Scheduled Date</label>
                                <input type="date" name="scheduled_date" value={form.scheduled_date} onChange={handle} className="mq-input" />
                            </div>
                            <div className="mq-modal-field">
                                <label>Estimated Cost ($)</label>
                                <input type="number" name="estimated_cost" value={form.estimated_cost} onChange={handle} className="mq-input" placeholder="0" />
                            </div>
                        </div>
                        <div className="mq-modal-field" style={{ marginTop: 12 }}>
                            <label>Description *</label>
                            <textarea name="description" value={form.description} onChange={handle} className="mq-textarea" required rows={4} placeholder="Describe the issue in detail…" />
                        </div>
                        <div className="mq-modal-field">
                            <label>Internal Notes</label>
                            <textarea name="notes" value={form.notes} onChange={handle} className="mq-textarea" rows={2} placeholder="Any internal notes…" />
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                            <button type="button" className="mq-btn-secondary" onClick={onClose}>Cancel</button>
                            <button type="submit" className="mq-btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create Request'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const MaintenanceQueue = () => {
    const [activeTab, setActiveTab] = useState('overview');

    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [properties, setProperties] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [propertyFilter, setPropertyFilter] = useState('all');
    const [viewMode, setViewMode] = useState('kanban');

    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showNewModal, setShowNewModal] = useState(false);

    const fetchRequests = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/maintenance-requests`);
            if (!res.ok) throw new Error('Failed to fetch');
            setRequests(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    const fetchProperties = useCallback(async () => {
        try { setProperties(await (await fetch(`${API_BASE_URL}/api/properties`)).json()); } catch (e) { console.error(e); }
    }, []);

    const fetchTenants = useCallback(async () => {
        try { setTenants(await (await fetch(`${API_BASE_URL}/api/tenants`)).json()); } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchRequests(); fetchProperties(); fetchTenants(); }, []);

    useEffect(() => {
        let f = [...requests];
        if (search) {
            const q = search.toLowerCase();
            f = f.filter(r =>
                r.request_type?.toLowerCase().includes(q) ||
                r.description?.toLowerCase().includes(q) ||
                r.property_name?.toLowerCase().includes(q) ||
                r.tenant_name?.toLowerCase().includes(q) ||
                r.unit_number?.toLowerCase().includes(q)
            );
        }
        if (statusFilter !== 'all') f = f.filter(r => r.status === statusFilter);
        if (priorityFilter !== 'all') f = f.filter(r => r.priority === priorityFilter);
        if (propertyFilter !== 'all') f = f.filter(r => String(r.property_id) === propertyFilter);
        setFilteredRequests(f);
    }, [requests, search, statusFilter, priorityFilter, propertyFilter]);

    const stats = {
        total: requests.length,
        pending: requests.filter(r => r.status === 'pending').length,
        inProgress: requests.filter(r => r.status === 'in_progress').length,
        completed: requests.filter(r => r.status === 'completed').length,
        overdue: requests.filter(isOverdue).length,
        totalCost: requests.reduce((s, r) => s + (Number(r.actual_cost) || Number(r.estimated_cost) || 0), 0),
    };

    const quickStatus = async (requestId, newStatus) => {
        try {
            await fetch(`${API_BASE_URL}/api/maintenance-requests/${requestId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            fetchRequests();
        } catch (e) { console.error(e); }
    };

    if (loading) return (
        <div className="mq-container">
            <Navbar selected="Maintenance" />
            <div className="mq-content"><div className="mq-loading"><div className="mq-spinner" /><p>Loading…</p></div></div>
        </div>
    );

    return (
        <div className="mq-container">
            <Navbar selected="Maintenance" />
            <div className="mq-content">

                <div className="mq-page-header">
                    <div>
                        <h1 className="mq-page-title">Maintenance</h1>
                        <p className="mq-page-subtitle">Work queue and portfolio overview</p>
                    </div>
                    <button className="mq-btn-primary mq-new-btn" onClick={() => setShowNewModal(true)}>
                        + New Request
                    </button>
                </div>

                {/* KPI row */}
                <div className="mq-kpi-row">
                    <KpiCard label="Total"       value={stats.total}           color="#3b82f6" icon="📋" />
                    <KpiCard label="Pending"     value={stats.pending}         color="#f59e0b" icon="🕐" />
                    <KpiCard label="In Progress" value={stats.inProgress}      color="#8b5cf6" icon="🔄" />
                    <KpiCard label="Completed"   value={stats.completed}       color="#10b981" icon="✅" />
                    <KpiCard label="Overdue"     value={stats.overdue}         color="#ef4444" icon="⚠️" sub={stats.overdue > 0 ? 'Needs attention' : 'All on track'} />
                    <KpiCard label="Total Cost"  value={fmt(stats.totalCost)}  color="#6b7280" icon="💰" sub="Est. + actual" />
                </div>

                {/* Tabs */}
                <div className="mq-tabs">
                    {[
                        { key: 'overview', label: 'Overview' },
                        { key: 'queue',    label: `Work Queue (${stats.pending + stats.inProgress})` },
                    ].map(tab => (
                        <button key={tab.key} className={`mq-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── OVERVIEW TAB ───────────────────────────────────────── */}
                {activeTab === 'overview' && (
                    <div className="mq-overview">
                        <div className="mq-overview-row">
                            <div className="mq-panel">
                                <h3 className="mq-panel-title">By Priority</h3>
                                {['urgent', 'high', 'medium', 'low'].map(p => {
                                    const count = requests.filter(r => r.priority === p).length;
                                    const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                                    return (
                                        <div key={p} className="mq-bar-row">
                                            <span className="mq-bar-label" style={{ color: PRIORITY_COLORS[p] }}>{p}</span>
                                            <div className="mq-bar-track"><div className="mq-bar-fill" style={{ width: `${pct}%`, background: PRIORITY_COLORS[p] }} /></div>
                                            <span className="mq-bar-count">{count}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mq-panel">
                                <h3 className="mq-panel-title">By Category</h3>
                                {['Plumbing', 'Electrical', 'HVAC', 'Structural', 'Appliance', 'Pest Control', 'Cleaning', 'General'].map(cat => {
                                    const count = requests.filter(r => getCategoryFromType(r.request_type) === cat).length;
                                    if (count === 0) return null;
                                    const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                                    return (
                                        <div key={cat} className="mq-bar-row">
                                            <span className="mq-bar-label">{CATEGORY_ICONS[cat]} {cat}</span>
                                            <div className="mq-bar-track"><div className="mq-bar-fill" style={{ width: `${pct}%`, background: '#3b82f6' }} /></div>
                                            <span className="mq-bar-count">{count}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mq-panel">
                                <h3 className="mq-panel-title">By Property</h3>
                                {properties.map(prop => {
                                    const count = requests.filter(r => r.property_id === prop.id).length;
                                    if (count === 0) return null;
                                    const open = requests.filter(r => r.property_id === prop.id && r.status !== 'completed' && r.status !== 'cancelled').length;
                                    return (
                                        <div key={prop.id} className="mq-property-row">
                                            <span className="mq-property-name">{prop.property_name || prop.name}</span>
                                            <span className="mq-property-total">{count} total</span>
                                            {open > 0 && <span className="mq-property-open">{open} open</span>}
                                        </div>
                                    );
                                })}
                                {properties.filter(p => requests.some(r => r.property_id === p.id)).length === 0 && (
                                    <p className="mq-empty-small">No requests yet</p>
                                )}
                            </div>
                        </div>

                        <div className="mq-panel mq-recent-panel">
                            <h3 className="mq-panel-title">Recent Activity</h3>
                            <div className="mq-recent-list">
                                {[...requests]
                                    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                                    .slice(0, 8)
                                    .map(r => (
                                        <div key={r.id} className="mq-recent-row" onClick={() => setSelectedRequest(r)}>
                                            <span className="mq-recent-icon">{CATEGORY_ICONS[getCategoryFromType(r.request_type)]}</span>
                                            <div className="mq-recent-info">
                                                <span className="mq-recent-type">{r.request_type}</span>
                                                <span className="mq-recent-prop">{r.property_name} · {r.unit_number || 'No unit'}</span>
                                            </div>
                                            <div className="mq-recent-meta">
                                                <PriorityBadge priority={r.priority} />
                                                <StatusBadge status={r.status} />
                                                <span className="mq-recent-time">{timeAgo(r.updated_at || r.created_at)}</span>
                                            </div>
                                        </div>
                                    ))
                                }
                                {requests.length === 0 && <p className="mq-empty-small">No maintenance requests yet.</p>}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── QUEUE TAB ──────────────────────────────────────────── */}
                {activeTab === 'queue' && (
                    <div className="mq-queue">
                        <div className="mq-filter-bar">
                            <input className="mq-search" placeholder="🔍  Search requests…" value={search} onChange={e => setSearch(e.target.value)} />
                            <select className="mq-select mq-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="all">All Statuses</option>
                                {STATUS_COLS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                            <select className="mq-select mq-filter-select" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
                                <option value="all">All Priorities</option>
                                {['urgent', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                            </select>
                            <select className="mq-select mq-filter-select" value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}>
                                <option value="all">All Properties</option>
                                {properties.map(p => <option key={p.id} value={String(p.id)}>{p.property_name || p.name}</option>)}
                            </select>
                            <div className="mq-view-toggle">
                                <button className={`mq-view-btn ${viewMode === 'kanban' ? 'active' : ''}`} onClick={() => setViewMode('kanban')}>⬛ Board</button>
                                <button className={`mq-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>☰ List</button>
                            </div>
                        </div>

                        {viewMode === 'kanban' ? (
                            <div className="mq-kanban">
                                {STATUS_COLS.map(col => {
                                    const colItems = filteredRequests.filter(r => r.status === col.key);
                                    return (
                                        <div key={col.key} className="mq-kanban-col">
                                            <div className="mq-kanban-col-header" style={{ borderTop: `3px solid ${col.color}` }}>
                                                <span style={{ color: col.color }}>{col.label}</span>
                                                <span className="mq-kanban-count">{colItems.length}</span>
                                            </div>
                                            <div className="mq-kanban-cards">
                                                {colItems.map(r => (
                                                    <div key={r.id} className={`mq-kanban-card ${isOverdue(r) ? 'overdue' : ''}`} onClick={() => setSelectedRequest(r)}>
                                                        <div className="mq-card-top">
                                                            <span className="mq-card-icon">{CATEGORY_ICONS[getCategoryFromType(r.request_type)]}</span>
                                                            <PriorityBadge priority={r.priority} />
                                                        </div>
                                                        <div className="mq-card-type">{r.request_type}</div>
                                                        <div className="mq-card-meta">🏢 {r.property_name || 'N/A'}{r.unit_number && ` · ${r.unit_number}`}</div>
                                                        <div className="mq-card-meta">👤 {r.tenant_name || 'No tenant'}</div>
                                                        {r.assigned_to && <div className="mq-card-meta">🔨 {r.assigned_to}</div>}
                                                        <div className="mq-card-footer">
                                                            <span>{timeAgo(r.created_at)}</span>
                                                            {r.estimated_cost && <span>{fmt(r.estimated_cost)}</span>}
                                                        </div>
                                                        {isOverdue(r) && <div className="mq-overdue-label">OVERDUE</div>}
                                                        <div className="mq-card-actions" onClick={e => e.stopPropagation()}>
                                                            {col.key !== 'pending'     && <button className="mq-move-btn" onClick={() => quickStatus(r.id, 'pending')}>← Pending</button>}
                                                            {col.key !== 'in_progress' && <button className="mq-move-btn" onClick={() => quickStatus(r.id, 'in_progress')}>In Progress</button>}
                                                            {col.key !== 'completed'   && <button className="mq-move-btn" onClick={() => quickStatus(r.id, 'completed')}>Completed ✓</button>}
                                                        </div>
                                                    </div>
                                                ))}
                                                {colItems.length === 0 && <div className="mq-col-empty">Nothing here</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="mq-list-view">
                                <div className="mq-list-header">
                                    <span>Type / Property</span><span>Tenant</span><span>Priority</span>
                                    <span>Status</span><span>Scheduled</span><span>Cost</span><span>Submitted</span>
                                </div>
                                {filteredRequests.map(r => (
                                    <div key={r.id} className={`mq-list-row ${isOverdue(r) ? 'overdue' : ''}`} onClick={() => setSelectedRequest(r)}>
                                        <span><strong>{CATEGORY_ICONS[getCategoryFromType(r.request_type)]} {r.request_type}</strong><br /><small>{r.property_name}{r.unit_number ? ` · ${r.unit_number}` : ''}</small></span>
                                        <span>{r.tenant_name || '—'}</span>
                                        <span><PriorityBadge priority={r.priority} /></span>
                                        <span><StatusBadge status={r.status} /></span>
                                        <span>{r.scheduled_date || '—'}</span>
                                        <span>{r.estimated_cost ? fmt(r.estimated_cost) : '—'}</span>
                                        <span>{timeAgo(r.created_at)}</span>
                                    </div>
                                ))}
                                {filteredRequests.length === 0 && <div className="mq-empty">No requests match your filters.</div>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {selectedRequest && (
                <RequestDetailModal request={selectedRequest} onClose={() => setSelectedRequest(null)} onStatusChange={fetchRequests} />
            )}
            {showNewModal && (
                <NewRequestModal properties={properties} tenants={tenants} onClose={() => setShowNewModal(false)} onCreated={fetchRequests} />
            )}
        </div>
    );
};

export default MaintenanceQueue;
